import moment from 'moment';

import { CdkDragEnd, CdkDragMove } from '@angular/cdk/drag-drop';

import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';

import { BookingDiaryEventModel } from './typings/booking-diary.event';

import {
  BookingDiaryColumnModel,
  BookingDiaryFoldedDaysModel
} from './typings/booking-diary.view';

import {
  BookingDiaryDragMoveModel,
  BookingDiaryEventDropModel,
  BookingDiaryMoveModel,
  BookingDiaryResizeModel,
  BookingDiarySlotModel
} from './typings/booking-diary.drag';

import {
  assignLanes,
  clampMinutes,
  CLICK_SUPPRESS_MS,
  DATE_FORMAT,
  DRAG_START_DELAY,
  formatMinutesLabel,
  SLOT_MINUTES,
  snapMinutes
} from './booking-diary.layout';

import {
  foldedDayLabel,
  isWeekendIndex
} from './booking-diary.weekend';

interface BookingDiaryGridColumn {
  blocks  : Array<BookingDiaryEventModel>;
  column  : BookingDiaryColumnModel;
  events  : Array<BookingDiaryEventModel>;
  isToday : boolean;
}

interface BookingDiaryHourLabel {
  label : string;
  top   : number;
}

// pixels a press may travel and still count as a click
const CLICK_TRAVEL   : number = 4;
const MAX_TEXT_LANES : number = 3;
const NOW_INTERVAL   : number = 60000;
const SLOT_HEIGHT    : string = '--booking-diary-slot-height';

// The hours of one day or one week: a column per mechanic or per day, a chip per booking
@Component({
  selector   : 'wor-booking-diary-time-grid',
  template: require('./booking-diary.time-grid.component.html')
})
export class BookingDiaryTimeGridComponent implements OnChanges, OnInit, OnDestroy {
  // which event field a column matches on: the mechanic in the day view, the date in the week view
  @Input() columnKey         : 'mechanicId' | 'date'                    = 'mechanicId';
  @Input() columns           : Array<BookingDiaryColumnModel>           = [];
  @Input() date              : string;
  @Input() events            : Array<BookingDiaryEventModel>            = [];
  @Input() foldedDays        : BookingDiaryFoldedDaysModel              = {};
  @Input() isBusy            : boolean                                  = false;

  // when set, each mechanic keeps a fixed lane (week view) instead of overlap-packed lanes
  @Input() laneOrder         : Array<string>                            = [];
  @Input() laneScroll        : { canNext : boolean; canPrev : boolean } = null;
  @Input() shopClosesMinutes : number                                   = 0;
  @Input() shopOpensMinutes  : number                                   = 0;

  @Output() columnSelected      : EventEmitter<string>                     = new EventEmitter<string>();
  @Output() dayFoldToggled      : EventEmitter<number>                     = new EventEmitter<number>();
  @Output() eventCancelled      : EventEmitter<BookingDiaryEventModel>     = new EventEmitter<BookingDiaryEventModel>();
  @Output() eventDragMoved      : EventEmitter<BookingDiaryDragMoveModel>  = new EventEmitter<BookingDiaryDragMoveModel>();
  @Output() eventDroppedOutside : EventEmitter<BookingDiaryEventDropModel> = new EventEmitter<BookingDiaryEventDropModel>();
  @Output() eventMoved          : EventEmitter<BookingDiaryMoveModel>      = new EventEmitter<BookingDiaryMoveModel>();
  @Output() eventOpened         : EventEmitter<BookingDiaryEventModel>     = new EventEmitter<BookingDiaryEventModel>();
  @Output() eventResized        : EventEmitter<BookingDiaryResizeModel>    = new EventEmitter<BookingDiaryResizeModel>();
  @Output() eventSplit          : EventEmitter<BookingDiaryEventModel>     = new EventEmitter<BookingDiaryEventModel>();
  @Output() laneScrolled        : EventEmitter<number>                     = new EventEmitter<number>();
  @Output() moveBlocked         : EventEmitter<void>                       = new EventEmitter<void>();
  @Output() rosterOpened        : EventEmitter<BookingDiaryEventModel>     = new EventEmitter<BookingDiaryEventModel>();
  @Output() slotPicked          : EventEmitter<BookingDiarySlotModel>      = new EventEmitter<BookingDiarySlotModel>();

  @ViewChild('gridBody') gridBody : ElementRef<HTMLElement>;

  dragStartDelay : { mouse : number; touch : number } = DRAG_START_DELAY;
  gridColumns    : Array<BookingDiaryGridColumn>      = [];
  gridHeight     : number                             = 0;
  hourLabels     : Array<BookingDiaryHourLabel>       = [];
  nowLeft        : number                             = 0;
  nowTop         : number                             = null;
  nowWidth       : number                             = 100;

  private blockClickTimer     : ReturnType<typeof setTimeout>;
  private blockPress          : { x : number; y : number };
  private hasJustDraggedBlock : boolean = false;
  private lastPointer         : { x : number; y : number };
  private nowInterval         : ReturnType<typeof setInterval>;
  private slotHeight          : number  = 0;

  constructor (
    private elementRef : ElementRef<HTMLElement>
  ) {}

  ngOnChanges () : void {
    const closes = this.shopClosesMinutes;
    const opens  = this.shopOpensMinutes;

    this.gridHeight = this.toPixels(closes - opens);
    this.hourLabels = [];

    for ( let hour = Math.ceil(opens / 60); hour <= Math.floor(closes / 60); hour++ ) {
      this.hourLabels.push({
        label: formatMinutesLabel(hour * 60),
        top  : this.toPixels(hour * 60 - opens)
      });
    }

    const today = moment().format(DATE_FORMAT);

    this.gridColumns = ( this.columns || [] ).map(column => {
      const forColumn = ( this.events || [] ).filter(event =>
        event[this.columnKey] === column.id
          && event.endMinutes > opens
          && event.startMinutes < closes);

      const bookings = forColumn.filter(event => event.kind === 'booking');

      return {
        column,
        // in the week a block sits in its mechanic's lane, so only lane mechanics' blocks are drawn
        blocks : forColumn.filter(event => event.kind !== 'booking')
          .filter(event => !this.laneOrder.length || this.laneOrder.indexOf(event.mechanicId) !== -1),
        events : this.laneOrder && this.laneOrder.length
          ? this.assignMechanicLanes(bookings)
          : assignLanes(bookings),
        isToday: this.columnKey === 'date' && column.id === today
      };
    });

    this.updateNowLine();

    // day columns are measured, so the line is placed again once they are drawn
    if ( this.columnKey === 'date' ) {
      setTimeout(() => this.updateNowLine());
    }
  }

  ngOnInit () : void {
    this.nowInterval = setInterval(() => this.updateNowLine(), NOW_INTERVAL);
  }

  ngOnDestroy () : void {
    clearInterval(this.nowInterval);
    clearTimeout(this.blockClickTimer);
  }

  blockLeft ( block : BookingDiaryEventModel ) : number {
    return this.laneOrder.length
      ? this.laneOrder.indexOf(block.mechanicId) / this.laneOrder.length * 100
      : 0;
  }

  blockWidth () : number {
    return this.laneOrder.length
      ? 100 / this.laneOrder.length
      : 100;
  }

  chipHeight ( event : BookingDiaryEventModel ) : number {
    const end   = Math.min(event.endMinutes, this.shopClosesMinutes);
    const start = Math.max(event.startMinutes, this.shopOpensMinutes);

    return this.toPixels(Math.max(end - start, SLOT_MINUTES));
  }

  chipLeft ( event : BookingDiaryEventModel ) : number {
    return event.lane / event.lanes * 100;
  }

  chipTop ( event : BookingDiaryEventModel ) : number {
    return Math.max(this.toPixels(event.startMinutes - this.shopOpensMinutes), 0);
  }

  chipWidth ( event : BookingDiaryEventModel ) : number {
    return 100 / event.lanes;
  }

  columnLabel ( index : number, label : string ) : string {
    return this.isFolded(index)
      ? foldedDayLabel(label)
      : label;
  }

  // only the week's columns are days; the day view's are mechanics and have no weekend to fold
  get hasFoldableColumns () : boolean {
    return this.columnKey === 'date';
  }

  isFolded ( index : number ) : boolean {
    return this.hasFoldableColumns && !!this.foldedDays[index];
  }

  // past this many lanes in a day column there is no room for text, so chips become colour lines
  get isLinesMode () : boolean {
    return this.columnKey === 'date' && !!this.laneOrder && this.laneOrder.length > MAX_TEXT_LANES;
  }

  // the moving booking is excluded from the check; pass null for one arriving from outside the grid
  isOccupied (
    event        : BookingDiaryEventModel,
    columnId     : string,
    laneId       : string,
    startMinutes : number,
    endMinutes   : number
  ) : boolean {
    const date = this.columnKey === 'date'
      ? columnId
      : this.date;

    const mechanicId = this.columnKey === 'date'
      ? laneId
      : columnId;

    return ( this.events || [] ).some(other => other.kind === 'booking'
      && ( !event || other.id !== event.id )
      && other.mechanicId === mechanicId
      && other.date === date
      && other.startMinutes < endMinutes
      && other.endMinutes > startMinutes);
  }

  isWeekend ( index : number ) : boolean {
    return this.hasFoldableColumns && isWeekendIndex(index);
  }

  // which column, and in the week view which lane inside it, a dropped booking has landed on
  locateFromRect ( rect : ClientRect ) : BookingDiarySlotModel {
    if ( !this.gridBody || !this.columns || !this.columns.length ) {
      return null;
    }

    const body      = this.gridBody.nativeElement.getBoundingClientRect();
    const centerX   = rect.left + rect.width / 2;
    const tolerance = this.toPixels(SLOT_MINUTES);

    // only a drop beside the grid is ignored; one above or below it lands on the edge of the day
    if ( centerX < body.left || centerX > body.right || rect.top < body.top - tolerance - body.height || rect.top > body.bottom + tolerance ) {
      return null;
    }

    const boxes = this.columnBoxes();

    let column = boxes.findIndex(box => centerX >= box.left && centerX < box.right);

    if ( column < 0 ) {
      column = centerX < body.left
        ? 0
        : this.columns.length - 1;
    }

    const box         = boxes[column];
    const columnLeft  = box
      ? box.left - body.left
      : 0;
    const columnWidth = box
      ? box.width
      : body.width / this.columns.length;

    const minutes = snapMinutes(this.shopOpensMinutes + this.toMinutes(rect.top - body.top));

    return {
      columnId    : this.columns[column].id,
      laneId      : this.laneFromOffset(centerX - body.left - columnLeft, columnWidth),
      startMinutes: clampMinutes(minutes, this.shopOpensMinutes, this.shopClosesMinutes - SLOT_MINUTES)
    };
  }

  // the click that follows a drag of an away block must not also open the roster
  onBlockClick ( clickEvent : MouseEvent, block : BookingDiaryEventModel ) : void {
    const press      = this.blockPress;
    const hasMoved   = press && Math.hypot(clickEvent.clientX - press.x, clickEvent.clientY - press.y) > CLICK_TRAVEL;

    if ( this.hasJustDraggedBlock || hasMoved ) {
      return;
    }

    this.rosterOpened.emit(block);
  }

  onBlockPress ( pressEvent : MouseEvent ) : void {
    this.blockPress = {
      x: pressEvent.clientX,
      y: pressEvent.clientY
    };
  }

  onChipResized ( endMinutes : number, event : BookingDiaryEventModel ) : void {
    const limit   = Math.min(this.shopClosesMinutes, this.nextBookingStart(event));
    const clamped = clampMinutes(endMinutes, event.startMinutes + SLOT_MINUTES, limit);

    if ( clamped === event.endMinutes ) {
      return;
    }

    this.eventResized.emit({
      event,
      endMinutes: clamped
    });
  }

  onColumnHeaderClick ( columnId : string ) : void {
    if ( this.columnKey === 'date' ) {
      this.columnSelected.emit(columnId);
    }
  }

  // the card snaps back; the page moves it for real once the drop is known to be free
  onDragEnded ( end : CdkDragEnd, event : BookingDiaryEventModel ) : void {
    const rect = end.source.element.nativeElement.getBoundingClientRect();

    end.source.reset();

    this.eventDragMoved.emit(null);

    if ( event.kind !== 'booking' ) {
      this.hasJustDraggedBlock = true;

      clearTimeout(this.blockClickTimer);

      this.blockClickTimer = setTimeout(() => this.hasJustDraggedBlock = false, CLICK_SUPPRESS_MS);
    }

    // the pointer, not the chip, says where it landed: over the header or the page it is no move
    const viewport = this.gridBody
      ? this.gridBody.nativeElement.parentElement.getBoundingClientRect()
      : null;
    const point    = end.dropPoint;
    const isOnGrid = !!viewport && point.x >= viewport.left && point.x <= viewport.right
      && point.y >= viewport.top && point.y <= viewport.bottom;
    const target   = isOnGrid
      ? this.locateFromRect(rect)
      : null;

    if ( !target ) {
      this.eventDroppedOutside.emit({
        event,
        rect,
        pointer: this.lastPointer
      });

      return;
    }

    const duration     = event.endMinutes - event.startMinutes;
    const startMinutes = clampMinutes(target.startMinutes, this.shopOpensMinutes, this.shopClosesMinutes - duration);
    const laneId       = target.laneId || event.mechanicId;

    const isUnmoved = startMinutes === event.startMinutes
      && target.columnId === event[this.columnKey]
      && laneId === event.mechanicId;

    if ( isUnmoved ) {
      return;
    }

    if ( this.isOccupied(event, target.columnId, laneId, startMinutes, startMinutes + duration) ) {
      this.moveBlocked.emit();

      return;
    }

    this.eventMoved.emit({
      event,
      laneId,
      startMinutes,
      columnId: target.columnId
    });
  }

  onDragMoved ( move : CdkDragMove, event : BookingDiaryEventModel ) : void {
    this.lastPointer = {
      x: move.pointerPosition.x,
      y: move.pointerPosition.y
    };

    const body = this.gridBody
      ? this.gridBody.nativeElement.getBoundingClientRect()
      : null;

    const isOutsideGrid = !body
      || move.pointerPosition.x < body.left
      || move.pointerPosition.x > body.right
      || move.pointerPosition.y < body.top
      || move.pointerPosition.y > body.bottom;

    this.eventDragMoved.emit({
      event,
      isOutsideGrid,
      x: move.pointerPosition.x,
      y: move.pointerPosition.y
    });
  }

  // a chip or block under the pointer is its own thing to click; only empty time picks a slot
  onGridDoubleClick ( clickEvent : MouseEvent ) : void {
    const target = clickEvent.target as HTMLElement;

    if ( target && target.closest('.booking-diary-chip-host, .booking-diary-block') ) {
      return;
    }

    // the slot under the pointer, not the nearest edge: half a slot up turns rounding into a floor
    const slot = this.locateFromRect({
      left : clickEvent.clientX,
      top  : clickEvent.clientY - this.toPixels(SLOT_MINUTES) / 2,
      width: 0
    } as ClientRect);

    if ( slot ) {
      this.slotPicked.emit(slot);
    }
  }

  trackColumn ( index : number, gridColumn : BookingDiaryGridColumn ) : string {
    return gridColumn.column.id;
  }

  trackEvent ( index : number, event : BookingDiaryEventModel ) : string {
    return `${ event.kind }-${ event.id }-${ event.date }`;
  }

  // bookings outside the visible lane window are left out; the header arrows page to them
  private assignMechanicLanes ( bookings : Array<BookingDiaryEventModel> ) : Array<BookingDiaryEventModel> {
    const visible = bookings.filter(booking => this.laneOrder.indexOf(booking.mechanicId) !== -1);

    visible.forEach(booking => {
      booking.lane  = this.laneOrder.indexOf(booking.mechanicId);
      booking.lanes = this.laneOrder.length;
    });

    return visible;
  }

  // the columns as drawn, read on demand: only a drop asks, and the widths can change between drops
  private columnBoxes () : Array<ClientRect> {
    if ( !this.gridBody ) {
      return [];
    }

    const elements = this.gridBody.nativeElement.querySelectorAll('.booking-diary-column');
    const boxes : Array<ClientRect> = [];

    for ( let index = 0; index < elements.length; index++ ) {
      boxes.push(( elements[index] as HTMLElement ).getBoundingClientRect());
    }

    return boxes;
  }

  // the week view splits a day column into a lane per mechanic; the position inside picks the lane
  private laneFromOffset ( offsetX : number, columnWidth : number ) : string {
    if ( !this.laneOrder || !this.laneOrder.length ) {
      return null;
    }

    const lane = Math.min(
      Math.max(Math.floor(offsetX / ( columnWidth / this.laneOrder.length )), 0),
      this.laneOrder.length - 1
    );

    return this.laneOrder[lane];
  }

  // a stretched booking may not run into the next booking for the same mechanic
  private nextBookingStart ( event : BookingDiaryEventModel ) : number {
    const followers = ( this.events || [] ).filter(other =>
      other.kind === 'booking'
        && other.id !== event.id
        && other.mechanicId === event.mechanicId
        && other[this.columnKey] === event[this.columnKey]
        && other.startMinutes > event.startMinutes);

    return followers.length
      ? Math.min(...followers.map(other => other.startMinutes))
      : Infinity;
  }

  private toMinutes ( pixels : number ) : number {
    const slotHeight = this.toPixels(SLOT_MINUTES);

    return slotHeight
      ? pixels / slotHeight * SLOT_MINUTES
      : 0;
  }

  // the slot height is a token the stylesheet publishes, so the geometry and the gridlines agree
  private toPixels ( minutes : number ) : number {
    if ( !this.slotHeight ) {
      const value = getComputedStyle(this.elementRef.nativeElement).getPropertyValue(SLOT_HEIGHT);

      this.slotHeight = parseFloat(value) || 0;
    }

    return minutes / SLOT_MINUTES * this.slotHeight;
  }

  private updateNowLine () : void {
    const now     = moment();
    const today   = now.format(DATE_FORMAT);
    const minutes = now.hours() * 60 + now.minutes();

    this.nowLeft  = 0;
    this.nowTop   = null;
    this.nowWidth = 100;

    if ( minutes < this.shopOpensMinutes || minutes > this.shopClosesMinutes ) {
      return;
    }

    if ( this.columnKey === 'date' ) {
      const index = ( this.columns || [] ).findIndex(column => column.id === today);

      if ( index === -1 ) {
        return;
      }

      // measured, since a folded day is narrower; equal shares stand in until the columns are drawn
      const body  = this.gridBody
        ? this.gridBody.nativeElement.getBoundingClientRect()
        : null;
      const boxes = this.columnBoxes();

      if ( body && body.width && boxes[index] ) {
        this.nowLeft  = ( boxes[index].left - body.left ) / body.width * 100;
        this.nowWidth = boxes[index].width / body.width * 100;
      }
      else {
        this.nowLeft  = index / this.columns.length * 100;
        this.nowWidth = 100 / this.columns.length;
      }
    }
    else if ( this.date !== today ) {
      return;
    }

    this.nowTop = this.toPixels(minutes - this.shopOpensMinutes);
  }
}
